import { useCallback, useEffect, useRef, useState } from "react";
import { App } from "antd";

import { ApiError } from "@/api";

/**
 * Loads data from the API and tracks loading/error state.
 *
 * Every page needs the same fetch → loading → error → refetch cycle, so it
 * lives here once. `deps` behaves like a useEffect dependency list: change
 * the selected unit and the request re-runs, with in-flight results from
 * the previous unit discarded rather than landing late.
 *
 *   const { data, loading, error, reload } = useApi(
 *     () => reports.getCapacity(unit), [unit]);
 */
export function useApi(request, deps = [], { skip = false, initial = null } = {}) {
  const [data, setData] = useState(initial);
  const [loading, setLoading] = useState(!skip);
  const [error, setError] = useState(null);
  const requestRef = useRef(request);
  requestRef.current = request;
  const runId = useRef(0);

  const reload = useCallback(async () => {
    const id = ++runId.current;
    setLoading(true);
    setError(null);
    try {
      const result = await requestRef.current();
      if (id === runId.current) setData(result);
      return result;
    } catch (err) {
      if (id === runId.current) setError(err);
      return undefined;
    } finally {
      if (id === runId.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (skip) {
      setLoading(false);
      return;
    }
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, skip]);

  return { data, loading, error, reload, setData };
}

/**
 * Runs a write and reports the outcome, so pages don't repeat try/catch
 * plus toast plumbing. Returns [run, busy]; `run` resolves to the result
 * or undefined when the call failed.
 *
 *   const [save, saving] = useAction(
 *     (payload) => masterData.calendar.save(unit, payload),
 *     { success: "Calendar saved" });
 *
 * `success` may also be a function of the result, for outcomes that are only
 * worth reporting in terms of what came back ("7 imported · 2 skipped").
 */
/**
 * Show one failure the way every failure in this app is shown.
 *
 * A notification, not a `message`: the server's refusals are whole sentences
 * ("Started 120 min late (allowed 30 min) — select a reason"), and antd's
 * `message` renders them as one centred line over the top of the page, where
 * a long one wraps badly, covers the toolbar it is talking about, and is gone
 * before it has been read.
 *
 * Exported so the handful of callers that catch their own errors show them in
 * the same place as the ones that go through `useAction`.
 */
export function notifyError(notification, err, title = "Could not complete that") {
  notification.error({
    message: title,
    description:
      err instanceof ApiError
        ? err.message
        : typeof err === "string"
          ? err
          : "Something went wrong",
    placement: "topRight",
    duration: 8,
    style: { width: 420 },
  });
}

export function useAction(action, { success, onDone } = {}) {
  const { message, notification } = App.useApp();
  const [busy, setBusy] = useState(false);

  const run = useCallback(
    async (...args) => {
      setBusy(true);
      try {
        const result = await action(...args);
        if (success) {
          // A success builder may return null to mean "handled elsewhere"
          // — an action that renders its own outcome in place should not
          // also raise an empty toast.
          const text =
            typeof success === "function" ? success(result) : success;
          if (text) message.success(text);
        }
        onDone?.(result);
        return result;
      } catch (err) {
        notifyError(notification, err);
        return undefined;
      } finally {
        setBusy(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [action, success, message, notification],
  );

  return [run, busy];
}

/** A job still on its way: waiting in the queue, or being worked. */
const ACTIVE = new Set(["queued", "running"]);

/**
 * Polls a background job (solve, debottleneck study) until it stops
 * running, then hands back the finished record. A job that is still QUEUED
 * (another optimization holds the worker) is polled exactly like a running
 * one — stopping there left the page waiting on a job it never asked about
 * again.
 */
export function useJobPolling(fetchJob, { intervalMs = 2000 } = {}) {
  const [job, setJob] = useState(null);
  const [jobId, setJobId] = useState(null);
  const timer = useRef(null);

  useEffect(() => {
    if (!jobId) return undefined;
    let cancelled = false;

    const poll = async () => {
      try {
        const next = await fetchJob(jobId);
        if (cancelled) return;
        setJob(next);
        if (ACTIVE.has(next.status)) {
          timer.current = setTimeout(poll, intervalMs);
        }
      } catch {
        if (!cancelled) setJob({ status: "error", error: "Lost contact with the job" });
      }
    };
    poll();

    return () => {
      cancelled = true;
      clearTimeout(timer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId, intervalMs]);

  const start = useCallback((id) => {
    setJob({ status: "running" });
    setJobId(id);
  }, []);

  const reset = useCallback(() => {
    clearTimeout(timer.current);
    setJobId(null);
    setJob(null);
  }, []);

  return { job, start, reset, running: ACTIVE.has(job?.status) };
}
